/**
 * Shared postMessage protocol types for Host <-> iFrame communication.
 *
 * The host script runs on the WordPress page and manages the iframe element,
 * overlay, and fixed-element adjustments.
 *
 * The iframe content runs inside the iframe and contains the React chat app.
 */

/**
 * Unique prefix for all messages to avoid conflicts with other scripts.
 */
export const MESSAGE_PREFIX = 'dhsv100-ai-chat' as const;

// ---------------------------------------------------------------------------
// iFrame -> Host messages
// ---------------------------------------------------------------------------

export interface IframeReadyMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'ready';
}

export interface IframeDrawerOpenMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'drawer-open';
	variant: 'default' | 'sidebar' | 'fullscreen';
}

export interface IframeDrawerCloseMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'drawer-close';
}

export interface IframeMessagesChangedMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'messages-changed';
	messages: Array< {
		id: string;
		role: 'user' | 'assistant';
		content: string;
		timestamp: number;
	} >;
}

export interface IframeStatusChangedMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'status-changed';
	status: 'idle' | 'streaming' | 'submitted' | 'error';
}

export interface IframeResponseDoneMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'response-done';
}

export interface IframeChatReadyMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'chat-ready';
}

export type IframeToHostMessage =
	| IframeReadyMessage
	| IframeDrawerOpenMessage
	| IframeDrawerCloseMessage
	| IframeMessagesChangedMessage
	| IframeStatusChangedMessage
	| IframeResponseDoneMessage
	| IframeChatReadyMessage;

// ---------------------------------------------------------------------------
// Host -> iFrame messages
// ---------------------------------------------------------------------------

export interface HostCloseRequestMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'close-request';
}

export interface HostExpandedMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'expanded';
}

export interface HostOpenRequestMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'open-request';
	variant?: 'default' | 'sidebar' | 'fullscreen';
}

export interface HostOpenWithMessageMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'open-with-message';
	text: string;
	variant?: 'default' | 'sidebar' | 'fullscreen';
	startNewChat?: boolean;
}

export interface HostSendMessageMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'send-message';
	text: string;
}

export interface HostNewChatMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'new-chat';
}

export interface HostClearHistoryMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'clear-history';
}

/**
 * The host page has been scrolled (or scrolled back to the top).
 *
 * The pill collapses to its glyph while the reader is moving down the page and
 * reopens on hover; scrolling is only observable on the host, hence the relay.
 */
export interface HostScrollStateMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'scroll-state';
	scrolled: boolean;
}

export interface HostSetViewMessage {
	type: typeof MESSAGE_PREFIX;
	action: 'set-view';
	view: 'popover' | 'history';
}

export type HostToIframeMessage =
	| HostCloseRequestMessage
	| HostExpandedMessage
	| HostOpenRequestMessage
	| HostOpenWithMessageMessage
	| HostSendMessageMessage
	| HostNewChatMessage
	| HostClearHistoryMessage
	| HostScrollStateMessage
	| HostSetViewMessage;

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

export function isIframeToHostMessage(
	data: unknown
): data is IframeToHostMessage {
	if ( ! data || typeof data !== 'object' ) {
		return false;
	}
	const msg = data as Record< string, unknown >;
	return msg.type === MESSAGE_PREFIX;
}

export function isHostToIframeMessage(
	data: unknown
): data is HostToIframeMessage {
	if ( ! data || typeof data !== 'object' ) {
		return false;
	}
	const msg = data as Record< string, unknown >;
	return msg.type === MESSAGE_PREFIX;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Send a message from the iframe to the host (parent window).
 * @param message
 */
export function sendToHost( message: IframeToHostMessage ): void {
	if ( window.parent && window.parent !== window ) {
		window.parent.postMessage( message, '*' );
	}
}

/**
 * Send a message from the host to the iframe.
 * @param iframe
 * @param message
 */
export function sendToIframe(
	iframe: HTMLIFrameElement,
	message: HostToIframeMessage
): void {
	if ( iframe.contentWindow ) {
		iframe.contentWindow.postMessage( message, '*' );
	}
}

/**
 * Check if the current window is inside an iframe.
 */
export function isInIframe(): boolean {
	try {
		return window.self !== window.top;
	} catch ( e ) {
		// If accessing window.top throws (cross-origin), we are in an iframe
		return true;
	}
}
